/**
 * Draft Service - Item Draft Management
 *
 * Manages drafts in localStorage for Item forms (task, transaction, note, keyDate)
 * with pending sub-items. Drafts survive page refresh and are automatically
 * cleaned up after 24h.
 */

import type { ItemType, CreateSubItemInput } from '@perfin/shared';
import { getRetainedFields, getStashedFields } from '@perfin/shared';

// ============================================================================
// TYPES
// ============================================================================

export interface PendingSubItem {
  tempId: string;
  input: CreateSubItemInput;
  pendingAttachments?: string[]; // base64 encoded for localStorage
}

/**
 * Represents a child item (attachment or sub-item) with its type and ID
 */
export interface ChildOrderEntry {
  type: 'attachment' | 'subItem';
  id: string;
}

/**
 * Snapshot of the original state when an item/sub-item was loaded for editing.
 * Used to detect unsaved changes.
 */
export interface OriginalState {
  formData: Record<string, unknown>;      // Snapshot on load (never overwritten)
  existingAttachmentIds: string[];        // Original attachment IDs
  existingSubItemIds: string[];           // Original sub-item IDs
  originalChildOrder?: ChildOrderEntry[]; // Original order of attachments and sub-items
  capturedAt: number;                     // Timestamp when captured
}

export interface ItemDraft {
  draftId: string;          // Unique ID for this draft
  itemId?: string;          // Set if editing existing item
  currentType: ItemType;
  formData: Record<string, unknown>;
  stashed: {
    [K in ItemType]?: Record<string, unknown>;
  };
  pendingSubItems: PendingSubItem[];
  pendingAttachments: string[]; // base64 encoded for localStorage
  lastModified: number;
  // Unsaved changes detection
  originalState?: OriginalState;          // Snapshot on load (never overwritten during edit)
  removedAttachmentIds: string[];         // Deferred deletion tracking
  removedSubItemIds: string[];            // Future use: deferred sub-item deletion
  reorderedChildren?: ChildOrderEntry[];  // Deferred reorder tracking (null = no reorder)
}

/**
 * Sub-item draft for tracking unsaved changes in SubItemEditorPage
 */
export interface SubItemDraft {
  draftId: string;
  parentId?: string;
  subItemId?: string;
  currentType: ItemType;
  formData: Record<string, unknown>;
  pendingAttachments: string[];
  lastModified: number;
  originalState?: OriginalState;
  removedAttachmentIds: string[];
}

// ============================================================================
// CONSTANTS
// ============================================================================

const ITEM_DRAFT_PREFIX = 'perfin_item_draft_';
const ITEM_DRAFT_LIST_KEY = 'perfin_item_draft_list';
const DRAFT_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

// ============================================================================
// ITEM DRAFT OPERATIONS
// ============================================================================

function getItemDraftKey(draftId: string): string {
  return `${ITEM_DRAFT_PREFIX}${draftId}`;
}

function generateDraftId(): string {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Save Item draft to localStorage
 */
export function saveItemDraft(draft: ItemDraft): void {
  try {
    const key = getItemDraftKey(draft.draftId);
    draft.lastModified = Date.now();
    localStorage.setItem(key, JSON.stringify(draft));

    // Update draft list index
    const listData = localStorage.getItem(ITEM_DRAFT_LIST_KEY);
    const draftList: string[] = listData ? JSON.parse(listData) : [];
    if (!draftList.includes(draft.draftId)) {
      draftList.push(draft.draftId);
      localStorage.setItem(ITEM_DRAFT_LIST_KEY, JSON.stringify(draftList));
    }
  } catch (error) {
    console.error('Failed to save item draft:', error);
  }
}

/**
 * Load Item draft from localStorage
 */
export function loadItemDraft(draftId: string): ItemDraft | null {
  try {
    const key = getItemDraftKey(draftId);
    const data = localStorage.getItem(key);

    if (!data) return null;

    const draft = JSON.parse(data) as ItemDraft;

    if (Date.now() - draft.lastModified > DRAFT_EXPIRY_MS) {
      clearItemDraft(draftId);
      return null;
    }

    return draft;
  } catch (error) {
    console.error('Failed to load item draft:', error);
    return null;
  }
}

/**
 * Load Item draft by itemId (for editing existing items)
 */
export function loadItemDraftByItemId(itemId: string): ItemDraft | null {
  try {
    const listData = localStorage.getItem(ITEM_DRAFT_LIST_KEY);
    const draftList: string[] = listData ? JSON.parse(listData) : [];

    for (const draftId of draftList) {
      const draft = loadItemDraft(draftId);
      if (draft && draft.itemId === itemId) {
        return draft;
      }
    }

    return null;
  } catch (error) {
    console.error('Failed to load item draft by itemId:', error);
    return null;
  }
}

/**
 * Load or create Item draft for new item creation
 */
export function loadOrCreateNewItemDraft(type: ItemType): ItemDraft {
  try {
    // Look for existing draft without itemId (new item)
    const listData = localStorage.getItem(ITEM_DRAFT_LIST_KEY);
    const draftList: string[] = listData ? JSON.parse(listData) : [];

    for (const draftId of draftList) {
      const draft = loadItemDraft(draftId);
      if (draft && !draft.itemId && draft.currentType === type) {
        return draft;
      }
    }
  } catch (error) {
    console.error('Failed to check existing drafts:', error);
  }

  // Create new draft
  return createItemDraft(type);
}

/**
 * Clear Item draft from localStorage
 */
export function clearItemDraft(draftId: string): void {
  try {
    const key = getItemDraftKey(draftId);
    localStorage.removeItem(key);

    // Update draft list index
    const listData = localStorage.getItem(ITEM_DRAFT_LIST_KEY);
    const draftList: string[] = listData ? JSON.parse(listData) : [];
    const filtered = draftList.filter((id) => id !== draftId);
    localStorage.setItem(ITEM_DRAFT_LIST_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error('Failed to clear item draft:', error);
  }
}

/**
 * Clear all expired Item drafts
 */
export function cleanupExpiredItemDrafts(): void {
  try {
    const listData = localStorage.getItem(ITEM_DRAFT_LIST_KEY);
    const draftList: string[] = listData ? JSON.parse(listData) : [];

    for (const draftId of draftList) {
      const key = getItemDraftKey(draftId);
      const data = localStorage.getItem(key);
      if (data) {
        const draft = JSON.parse(data) as ItemDraft;
        if (Date.now() - draft.lastModified > DRAFT_EXPIRY_MS) {
          clearItemDraft(draftId);
        }
      } else {
        // Draft data missing, remove from list
        clearItemDraft(draftId);
      }
    }
  } catch (error) {
    console.error('Failed to cleanup expired drafts:', error);
  }
}

/**
 * Create an empty Item draft
 */
export function createItemDraft(type: ItemType, itemId?: string): ItemDraft {
  return {
    draftId: generateDraftId(),
    itemId,
    currentType: type,
    formData: {},
    stashed: {},
    pendingSubItems: [],
    pendingAttachments: [],
    lastModified: Date.now(),
    // Unsaved changes detection - initialized empty, populated by captureOriginalState
    originalState: undefined,
    removedAttachmentIds: [],
    removedSubItemIds: [],
  };
}

/**
 * Create an empty Sub-item draft
 */
export function createSubItemDraft(
  type: ItemType,
  parentId?: string,
  subItemId?: string
): SubItemDraft {
  return {
    draftId: generateDraftId(),
    parentId,
    subItemId,
    currentType: type,
    formData: {},
    pendingAttachments: [],
    lastModified: Date.now(),
    originalState: undefined,
    removedAttachmentIds: [],
  };
}

/**
 * Add pending sub-item to draft
 */
export function addPendingSubItem(draft: ItemDraft, input: CreateSubItemInput): PendingSubItem {
  const pendingSubItem: PendingSubItem = {
    tempId: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    input,
    pendingAttachments: [],
  };
  draft.pendingSubItems.push(pendingSubItem);
  draft.lastModified = Date.now();
  return pendingSubItem;
}

/**
 * Update pending sub-item in draft
 */
export function updatePendingSubItem(
  draft: ItemDraft,
  tempId: string,
  input: Partial<CreateSubItemInput>
): void {
  const idx = draft.pendingSubItems.findIndex((s) => s.tempId === tempId);
  if (idx !== -1) {
    draft.pendingSubItems[idx].input = {
      ...draft.pendingSubItems[idx].input,
      ...input,
    } as CreateSubItemInput;
    draft.lastModified = Date.now();
  }
}

/**
 * Remove pending sub-item from draft
 */
export function removePendingSubItem(draft: ItemDraft, tempId: string): void {
  draft.pendingSubItems = draft.pendingSubItems.filter((s) => s.tempId !== tempId);
  draft.lastModified = Date.now();
}

/**
 * Reorder pending sub-items in draft
 * @param orderedTempIds Array of tempIds in the new order
 */
export function reorderPendingSubItems(draft: ItemDraft, orderedTempIds: string[]): void {
  const reordered: PendingSubItem[] = [];
  for (const tempId of orderedTempIds) {
    const item = draft.pendingSubItems.find((s) => s.tempId === tempId);
    if (item) {
      reordered.push(item);
    }
  }
  // Add any items not in the ordered list at the end (shouldn't happen, but safe)
  for (const item of draft.pendingSubItems) {
    if (!orderedTempIds.includes(item.tempId)) {
      reordered.push(item);
    }
  }
  draft.pendingSubItems = reordered;
  draft.lastModified = Date.now();
}

/**
 * Handle Item type change with field stashing (similar to SubItem)
 */
export function handleItemTypeChange(
  draft: ItemDraft,
  currentFormData: Record<string, unknown>,
  fromType: ItemType,
  toType: ItemType
): { updatedDraft: ItemDraft; newFormData: Record<string, unknown> } {
  // 1. Get fields to stash
  const fieldsToStash = getStashedFields(fromType, toType);

  // 2. Extract values to stash
  const stashedValues: Record<string, unknown> = {};
  for (const field of fieldsToStash) {
    const value = currentFormData[field];
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value) && value.length === 0) continue;
      stashedValues[field] = value;
    }
  }

  // 3. Save stashed values
  if (Object.keys(stashedValues).length > 0) {
    draft.stashed[fromType] = {
      ...draft.stashed[fromType],
      ...stashedValues,
    };
  }

  // 4. Update draft type
  draft.currentType = toType;

  // 5. Get retained fields
  const retainedFields = getRetainedFields(fromType, toType);

  // 6. Build new form data
  const newFormData: Record<string, unknown> = {};
  for (const field of retainedFields) {
    if (currentFormData[field] !== undefined) {
      newFormData[field] = currentFormData[field];
    }
  }

  // 7. Restore previously stashed values
  const previouslyStashed = draft.stashed[toType];
  if (previouslyStashed) {
    for (const [field, value] of Object.entries(previouslyStashed)) {
      if (newFormData[field] === undefined) {
        newFormData[field] = value;
      }
    }
  }

  // 8. Set default status
  // IMPORTANT: Keep in sync with types/index.ts (TaskStatus, TransactionStatus, NoteKeyDateStatus)
  if (toType === 'task') {
    newFormData.status = newFormData.status || 'new';
  } else {
    // Transaction, Note, KeyDate all use draft/final
    newFormData.status = newFormData.status || 'draft';
  }

  // 9. Update draft
  draft.formData = newFormData;
  draft.lastModified = Date.now();

  return { updatedDraft: draft, newFormData };
}
